import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { X, Info, AlertTriangle, CheckCircle2 } from "lucide-react";
import { renderRichAnnouncement } from "../lib/announcementFormat";
import { useSettings } from "../context/SettingsContext";

const TONE_META = {
  info:    { icon: Info,          barCls: "bg-teal-50 border-teal-200 text-teal-900",       iconCls: "text-teal-600" },
  warning: { icon: AlertTriangle, barCls: "bg-amber-50 border-amber-200 text-amber-900",    iconCls: "text-amber-600" },
  success: { icon: CheckCircle2,  barCls: "bg-emerald-50 border-emerald-200 text-emerald-900", iconCls: "text-emerald-600" },
};

/** Site-wide announcement banner. Fetches the current active announcement,
 *  respects per-user dismissals, and re-fetches when the admin publishes or
 *  retracts (via a window `amino:announcement-changed` event).
 */
export default function AnnouncementBanner() {
  const [ann, setAnn] = useState(null);
  const [hidden, setHidden] = useState(false);
  const { settings } = useSettings();
  // Wait for settings to arrive before firing any /announcements/active call.
  const flags = settings?.feature_flags;
  const enabled = flags == null ? null : flags.announcements !== false;

  const load = () => {
    if (enabled !== true) { setAnn(null); return; }
    api.get("/announcements/active")
      .then(({ data }) => {
        setAnn(data.announcement || null);
        setHidden(false);
      })
      .catch(() => setAnn(null));
  };

  useEffect(() => {
    load();
    const onChange = () => load();
    window.addEventListener("amino:announcement-changed", onChange);
    // Refresh once every 5 minutes so long-tab visitors eventually see new banners
    const t = setInterval(load, 5 * 60 * 1000);
    return () => {
      window.removeEventListener("amino:announcement-changed", onChange);
      clearInterval(t);
    };
  }, [enabled]);

  const dismiss = () => {
    if (!ann) return;
    setHidden(true); // optimistic
    api.post(`/announcements/${ann.id}/dismiss`).catch(() => setHidden(false));
  };

  if (enabled !== true || !ann || hidden) return null;

  const meta = TONE_META[ann.tone] || TONE_META.info;
  const Icon = meta.icon;

  return (
    <div className={`border-b ${meta.barCls}`}
         role="status"
         data-testid="announcement-banner">
      <div className="max-w-6xl mx-auto px-4 py-2.5 flex items-start gap-3">
        <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${meta.iconCls}`} />
        <div className="text-sm flex-1 min-w-0 whitespace-pre-wrap" data-testid="announcement-banner-body"
             dangerouslySetInnerHTML={{ __html: renderRichAnnouncement(ann.body) }} />
        <button
          type="button"
          onClick={dismiss}
          className="shrink-0 opacity-70 hover:opacity-100 transition-opacity p-1 -m-1 rounded"
          aria-label="Dismiss announcement"
          data-testid="announcement-banner-dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
